// Shared, dependency-free duration formatting. Both the editor-tree and
// FanWeb-tree duration mixins import it and the studio node unit suite requires
// it directly, so it is CJS, no deps.
//
// Input is always whole seconds (EpisodeDataModel::FIELD_DURATION is an int).

const FORMAT_WORDS = "words";
const FORMAT_COMPACT = "compact";
const FORMAT_CLOCK = "clock";
const FORMAT_CLOCK_SHORT = "clockShort";
const FORMAT_MINUTES = "minutes";

const DURATION_FORMAT_KEYS = [
  FORMAT_WORDS,
  FORMAT_COMPACT,
  FORMAT_CLOCK,
  FORMAT_CLOCK_SHORT,
  FORMAT_MINUTES,
];

const DURATION_FORMAT_ITEMS = [
  { id: FORMAT_WORDS, name: "Words — 1 hr 22 min" },
  { id: FORMAT_COMPACT, name: "Compact — 1h 22m" },
  { id: FORMAT_CLOCK, name: "Clock — 1:21:20" },
  { id: FORMAT_CLOCK_SHORT, name: "Clock, short — 81:20" },
  { id: FORMAT_MINUTES, name: "Total minutes — 82 min" },
];

// The words/compact/minutes shapes round a part-minute up, because "0 min" for a
// 40-second episode reads as broken. The clock shapes show real elapsed time, so
// they never round.
function roundedMinutes(totalSeconds) {
  return Math.max(1, Math.ceil(totalSeconds / 60));
}

function pad(value) {
  return String(value).padStart(2, "0");
}

// A duration of 0 (or missing, or negative) is an absence, not a quantity —
// there is no such thing as a zero-length episode — so it stays empty. This is
// deliberately unlike a like count, where 0 is a real value that must render.
function formatDuration(seconds, format = FORMAT_WORDS) {
  const totalSeconds = Math.floor(Number(seconds));
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return "";
  }

  const totalMinutes = roundedMinutes(totalSeconds);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  switch (format) {
    case FORMAT_COMPACT:
      return hours && minutes ? `${hours}h ${minutes}m` : hours ? `${hours}h` : `${minutes}m`;

    case FORMAT_CLOCK: {
      const clockHours = Math.floor(totalSeconds / 3600);
      const clockMinutes = Math.floor(totalSeconds % 3600 / 60);
      const clockSeconds = totalSeconds % 60;
      return clockHours
        ? `${clockHours}:${pad(clockMinutes)}:${pad(clockSeconds)}`
        : `${clockMinutes}:${pad(clockSeconds)}`;
    }

    case FORMAT_CLOCK_SHORT:
      return `${Math.floor(totalSeconds / 60)}:${pad(totalSeconds % 60)}`;

    case FORMAT_MINUTES:
      return `${totalMinutes} min`;

    // FORMAT_WORDS — the shape every already-authored duration renders today.
    default:
      if (!hours) {
        return `${totalMinutes} min`;
      }
      return minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`;
  }
}

module.exports = {
  FORMAT_WORDS,
  DURATION_FORMAT_KEYS,
  DURATION_FORMAT_ITEMS,
  formatDuration,
};
